// src/components/Services/ServiceOrders/ServiceOrdersTable.tsx
import { ServiceOrderSummary } from '@/types/service';
import { ServiceOrderStatusBadge } from './ServiceOrderStatusBadge';
import { formatCurrency } from '@/utils/formatters';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Wrench } from 'lucide-react';

interface ServiceOrdersTableProps {
  orders: ServiceOrderSummary[];
  onViewDetails: (id: string) => void;
  isLoading?: boolean;
}

export function ServiceOrdersTable({
  orders,
  onViewDetails,
  isLoading,
}: ServiceOrdersTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-12 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
        <Wrench className="h-8 w-8 opacity-40" />
        <p className="text-sm">Nenhuma ordem de serviço encontrada</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nº OS</TableHead>
          <TableHead>Cliente</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Equipamentos</TableHead>
          <TableHead>Responsável</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead>Criada</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.map((order) => (
          <TableRow
            key={order.id}
            className="cursor-pointer hover:bg-muted/50"
            onClick={() => onViewDetails(order.id)}
          >
            <TableCell className="font-mono text-sm font-medium">
              {order.service_order_number}
            </TableCell>
            <TableCell className="max-w-[200px] truncate">
              {order.customer_name}
            </TableCell>
            <TableCell>
              <ServiceOrderStatusBadge status={order.status} />
            </TableCell>
            <TableCell>
              <Badge variant="outline" className="text-xs">
                {order.equipment_count}{' '}
                {order.equipment_count === 1 ? 'equipamento' : 'equipamentos'}
              </Badge>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {order.responsible_name ?? '—'}
            </TableCell>
            <TableCell className="text-right font-medium">
              {formatCurrency(order.total_value)}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(order.created_at), {
                addSuffix: true,
                locale: ptBR,
              })}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
